import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Alert } from '@mui/material';
import { useAuthContext } from '../auth/useAuthContext';
import axios from '../utils/Axios';
import saveErrorMessage from '../utils/saveErrorMessage';
import PaymentSettingsForm from 'src/sections/@dashboard/payment_settings/Form';

export default function PaymentSettingsPage() {
  const { user, setGlobalUser } = useAuthContext();
  const [status, setStatus] = useState(null);
  const dismissTimer = useRef(null);

  useEffect(() => () => clearTimeout(dismissTimer.current), []);

  // A rejected save is shown with the server's own message and re-raised, so the form
  // can record the save as unsuccessful and keep its unsaved-changes warning. The form
  // catches it; see payment_settings/Form.js.
  const updateUser = useCallback(async (userData) => {
    clearTimeout(dismissTimer.current);

    // Only the request is inside the try, so a failure after a successful save is not
    // reported as a failed save.
    let response;
    try {
      response = await axios.put(`/user`, userData);
    } catch (error) {
      console.error('Payment Settings save failed', error);
      setStatus({ severity: 'error', message: saveErrorMessage(error) });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      throw error;
    }

    setGlobalUser(response.data.user);
    setStatus({ severity: 'success', message: 'Settings Updated.' });
    dismissTimer.current = setTimeout(() => setStatus(null), 6000);
  }, [setGlobalUser]);

  return (
    <>
      <Helmet>
        <title>Payment Settings | {localStorage.getItem("companyName") ?? "FlowBookings"}</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4">
          Payment Settings
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, mt: 1 }} style={{ color: "#637381", fontSize: 13, fontWeight: 400 }}>
          Configure how you wish to receive payments for your bookings on this screen
        </Typography>

        {status != null && (
          <Alert
            severity={status.severity}
            onClose={status.severity === 'error' ? () => setStatus(null) : undefined}
            sx={{ mt: 2, mb: 2, whiteSpace: 'pre-line' }}
          >
            {status.message}
          </Alert>
        )}

        <PaymentSettingsForm config={user.config} updateUser={updateUser} />
      </Container>
    </>
  );
}
